import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import random from 'lodash/random';

import imgixConfig from 'Config/imgix';
import RevealPrizeStep from 'Components/Order/KeyReveals/RevealPrizeStep';
import ProductCover from 'Components/Utility/ProductCover';
import useViewport from 'Hooks/useViewport';

import 'Components/Order/KeyReveals/ScratchCard/ScratchCardReveal.scss';

export default function ScratchCardReveal({
  uniqueId,
  testReveal,
  item,
  viewKeys,
  mysteryGameDetails,
}) {
  const [canvas, setCanvas] = useState();
  const [showKey, setShowKey] = useState(false);
  const [showCover, setShowCover] = useState(false);
  const canvasRef = useRef(canvas);
  const testCoverRef = useRef('');

  const { isAboveXl } = useViewport();

  useEffect(() => {
    let scratching = false;
    canvasRef.current = document.getElementById(`scratch-${uniqueId}`);
    setCanvas(canvasRef.current);

    const context = canvas && canvas.getContext('2d');

    if (context) {
      context.globalCompositeOperation = 'source-over';
      context.beginPath();

      const scratchImg = new Image();
      scratchImg.crossOrigin = 'anonymous';
      scratchImg.src = 'https://fanatical.imgix.net/assets/mystery/scratchcard/scratch-here.png?auto=compress,format&w=300&fit=crop&h=169';
      if (isAboveXl) {
        scratchImg.src = 'https://fanatical.imgix.net/assets/mystery/scratchcard/scratch-here.png?auto=compress,format&w=380&fit=crop&h=214';
      }
      scratchImg.onload = () => {
        const pattern = context.createPattern(scratchImg, 'repeat');
        context.fillStyle = pattern;
        context.rect(0, 0, 300, 169);
        if (isAboveXl) {
          context.rect(0, 0, 380, 214);
        }
        context.fill();
        setShowCover(true);
      };

      if (showCover) {
        context.fillStyle = '#BDBDBD';
        context.fill();
      }
    }

    function judgeVisible() {
      let imageData = context.getImageData(0, 0, 300, 169);
      if (isAboveXl) {
        imageData = context.getImageData(0, 0, 380, 214);
      }
      const pixels = imageData.data;
      const result = {};
      let i;
      let len = pixels.length;

      for (i = 3, len = pixels.length; i < len; i += 4) {
        /* eslint-disable no-unused-expressions */
        result[pixels[i]] || (result[pixels[i]] = 0);
        result[pixels[i]] += 1;
      }

      const fullPanelCount = result[255];
      const scratchOffCount = result[0];

      // If the user scratches off half of the canvas, show the key
      if (fullPanelCount < scratchOffCount) {
        window.setTimeout(() => {
          setShowKey(true);
        }, 1000);
      }
    }

    function clearArc(x, y) {
      if (scratching) {
        context.globalCompositeOperation = 'destination-out';
        context.beginPath();
        context.arc(x, y, 15, 0, Math.PI * 2, false); // arc(x, y, radius, startAngle, endAngle, counterclockwise)
        context.fill();
      }
    }

    // This function listens for touch events and converts them to mouse events
    function touchHandler(event) {
      event.preventDefault();
      const touch = event.changedTouches[0];
      const simulatedEvent = new MouseEvent({
        touchstart: 'mousedown',
        touchmove: 'mousemove',
        touchend: 'mouseup',
      }[event.type], {
        bubbles: true,
        cancelable: true,
        view: window,
        detail: 1,
        screenX: touch.screenX,
        screenY: touch.screenY,
        clientX: touch.clientX,
        clientY: touch.clientY,
        ctrlKey: false,
        altKey: false,
        shiftKey: false,
        metaKey: false,
        button: 0,
        relatedTarget: null,
      });

      touch.target.dispatchEvent(simulatedEvent);
    }

    if (canvas) {
      // Mouse Events
      canvas.addEventListener('mousedown', (event) => {
        scratching = true;
        clearArc(event.offsetX, event.offsetY);
        judgeVisible();
      }, false);

      canvas.addEventListener('mousemove', (event) => {
        if (!scratching) {
          return;
        }
        clearArc(event.offsetX, event.offsetY);
        judgeVisible();
      }, false);

      canvas.addEventListener('mouseup', () => {
        scratching = false;
      }, false);

      canvas.addEventListener('mouseleave', () => {
        scratching = false;
      }, false);

      // Mobile Touch Events
      canvas.addEventListener('touchstart', touchHandler, true);
      canvas.addEventListener('touchmove', touchHandler, true);
      canvas.addEventListener('touchend', touchHandler, true);
    }

    // Remove EventListeners
    return () => {
      if (canvas) {
        canvas.removeEventListener('mousedown', () => {});
        canvas.removeEventListener('mousemove', () => {});
        canvas.removeEventListener('mouseup', () => {});
        canvas.removeEventListener('mouseleave', () => {});
        canvas.removeEventListener('touchstart', () => {});
        canvas.removeEventListener('touchmove', () => {});
        canvas.removeEventListener('touchend', () => {});
      }
    };
  }, [canvas, setCanvas, uniqueId, testReveal, isAboveXl, showCover]);

  useEffect(() => {
    const testKeys = [
      'abbeed05-5d2e-487f-a523-9cc08b5cd8d8.jpeg', // Troublemaker
      'c51ecb7a-1384-4534-b794-6cd78f54ffbc.jpeg', // Fell Seal: Arbiter's Mark
      '1d6fbcdd-c8b2-45b6-a13d-00ec560cbf25.jpeg', // 77p egg: Eggwife
      'b4b9b83f-dc18-4c5d-a41b-03a9a0330a83.jpeg', // Mindustry
      '5b5b77a1-dfef-4643-ac17-fe0712d6d32d.jpeg', // Valfaris
      '85096f90-42cf-42a2-8e29-635e53dd8076.jpeg', // REAVER
      '85b5003e-7b70-4a20-9b33-e3be117b8b56.jpeg', // Sands of Aura
      '04fb89f9-c727-4671-a7d5-58227b07b49e.jpeg', // ITORAH
      '005e3e50-a243-4eb0-ad61-eeacb942eef5.jpeg', // Teslagrad Remastered
      '5b121a0d-b1fd-4081-8137-e4e83ca2268d.jpeg', // FORECLOSED
      'bd0b0cdc-cf6d-4026-bf1e-e8957ce0744c.jpeg', // Indiana Jones and the Great Circle
      '1109ab70-a49c-476a-bc1f-025a5ae82e60.jpeg', // Kingdom Come Deliverance II Gold
      'e7c1a243-30d2-4b1e-9b21-7fe9d30f5910.jpeg', // Marvel's Spider-Man 2
      '44f1ff65-621c-45e6-b82d-5d90c40eb3e2.jpeg', // NINJA GAIDEN 2 Black
    ];
    const randomNum = random(0, testKeys.length - 1);
    testCoverRef.current = testKeys[randomNum];
  }, []);

  let cover = 'showcase-sale-random-prize.jpg';
  let name = '';


  if (showCover) {
    if (item.name) name = item.name;
    if (get(mysteryGameDetails, 'cover')) cover = mysteryGameDetails.cover;
    if (get(mysteryGameDetails, 'name')) name = mysteryGameDetails.name;
    if (testReveal) cover = testCoverRef.current;
  }

  return (
    <div className="ScratchCardReveal">
      <div className="ScratchCardReveal__card">
        <div className="ScratchCardReveal__card__leftContainer">
          <div className="ScratchCardReveal__card__scratchLogo">
            <img
              src="https://cdn.fanatical.com/production/assets/mystery/scratchcard/2025/gaming-legends-logo.png"
              alt="Gaming Legends Sale Bonus Scratchcard"
            />
          </div>
          <div className="ScratchCardReveal__card__text">
            {!testReveal &&
              <FormattedMessage id="scratchcard-reveal.scratch-text" defaultMessage="Scratch off the panel to see what you have won!" />
            }
            {testReveal &&
              <FormattedMessage id="scratchcard-reveal.scratch-text.test" defaultMessage="Scratch off the panel to see what you could win!" />
            }
          </div>
        </div>

        <div className="ScratchCardReveal__card__rightContainer">
          <div className="ScratchCardReveal__card__scratchContainer">
            <div className="ScratchCardReveal__card__scratchContainer__cover">
              {!testReveal &&
                <ProductCover
                  cover={cover}
                  alt={`Product cover for ${name}`}
                  sizesOverride="(min-width: 1520px) 380px, 300px"
                />
              }
              {testReveal &&
                <div className="responsive-image-island">
                  <div className="responsive-image responsive-image--16by9">
                    <img
                      className="img-fluid img-full img-force-full"
                      src={`https://fanatical.imgix.net/product/original/${cover}?${imgixConfig}`}
                      alt={name}
                    />
                  </div>
                </div>
              }
            </div>
            {isAboveXl &&
              <canvas id={`scratch-${uniqueId}`} width="380" height="214" className="ScratchCardReveal__card__scratchContainer__canvas">
                Your browser does not support the scratch card element.
                Please refresh to see your free gift.
              </canvas>
            }
            {!isAboveXl &&
              <canvas id={`scratch-${uniqueId}`} width="300" height="169" className="ScratchCardReveal__card__scratchContainer__canvas">
                Your browser does not support the scratch card element.
                Please refresh to see your free gift.
              </canvas>
            }
          </div>
        </div>
      </div>

      {showKey &&
        <RevealPrizeStep
          type="scratchcard"
          testReveal={testReveal}
          viewKeys={viewKeys}
          prizeName={name}
        />
      }
    </div>
  );
}

ScratchCardReveal.propTypes = {
  item: PropTypes.shape({}),
  mysteryGameDetails: PropTypes.shape({}),
  testReveal: PropTypes.bool,
  uniqueId: PropTypes.string,
  viewKeys: PropTypes.func,
};

ScratchCardReveal.defaultProps = {
  item: {},
  mysteryGameDetails: {},
  testReveal: false,
  uniqueId: '',
  viewKeys: () => {},
};
